import { useState, useEffect, useRef, useMemo } from 'react';
import { useLanguage, SUPPORTED_LANGUAGES, SUPPORTED_COUNTRIES } from '../hooks/useLanguage';
import { useTranslation } from '../hooks/useTranslation';
import { Globe, X, Check } from 'lucide-react';

export default function LocaleSelector() {
  const {
    country,
    language,
    setLocale,
    currentCountry,
    currentLanguage
  } = useLanguage();

  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const modalRef = useRef(null);

  // Tupel-Liste generieren: Country x Languages, sortiert nach Sprache → Land
  const localeOptions = useMemo(() => {
    const options = [];
    Object.values(SUPPORTED_COUNTRIES).forEach(c => {
      c.languages.forEach(langCode => {
        const lang = SUPPORTED_LANGUAGES[langCode];
        if (lang) {
          options.push({
            country: c,
            language: lang,
            countryCode: c.code,
            languageCode: langCode,
            isSelected: c.code === country && langCode === language,
          });
        }
      });
    });

    // Sortierung: aktuelle Auswahl ganz oben, dann nach Sprache gruppiert,
    // innerhalb jeder Sprache alphabetisch nach Landesname
    options.sort((a, b) => {
      // 1. Aktuelle Auswahl immer oben
      if (a.isSelected) return -1;
      if (b.isSelected) return 1;

      // 2. Nach Sprach-Name gruppieren (alphabetisch)
      const langCompare = a.language.nameNative.localeCompare(b.language.nameNative);
      if (langCompare !== 0) return langCompare;

      // 3. Innerhalb gleicher Sprache: nach Landesname
      return a.country.nameNative.localeCompare(b.country.nameNative);
    });

    return options;
  }, [country, language]);

  // ESC zum Schließen
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Click outside zum Schließen
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = (countryCode, languageCode) => {
    setLocale(countryCode, languageCode);
    setIsOpen(false);
  };

  return (
    <>
      {/* Header Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="locale-btn"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 10px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          cursor: 'pointer',
          color: 'var(--text-primary)',
          fontSize: '1rem',
          fontWeight: 500,
          transition: 'all 0.15s',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--primary)';
          e.currentTarget.style.background = 'var(--bg-tertiary)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'var(--border)';
          e.currentTarget.style.background = 'var(--bg-secondary)';
        }}
      >
        <Globe size={16} className="locale-globe" aria-hidden="true" />
        <span>{country.toUpperCase()}</span>
        <span className="locale-sep" style={{ color: 'var(--text-tertiary)' }}>|</span>
        <span className="locale-lang-name">{currentLanguage?.nameNative || language}</span>
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'color-mix(in srgb, var(--schwarz) 50%, transparent)',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '10vh',
            zIndex: 10000,
          }}
        >
          <div
            ref={modalRef}
            style={{
              background: 'var(--bg-primary)',
              borderRadius: 'var(--radius-lg)',
              width: '90%',
              maxWidth: '480px',
              maxHeight: '80vh',
              overflow: 'hidden',
              boxShadow: 'var(--shadow-dialog)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              padding: '20px 24px',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
            }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                  {t('locale.title', 'Language & Region')}
                </h2>
                <p style={{
                  margin: '4px 0 0 0',
                  fontSize: '1rem',
                  color: 'var(--text-secondary)',
                }}>
                  {t('locale.subtitle', 'Choose the language for the user interface')}
                </p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  color: 'var(--text-secondary)',
                  borderRadius: 'var(--radius-round)',
                  flexShrink: 0,
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Tupel-Liste */}
            <div style={{
              padding: '12px 24px',
              overflowY: 'auto',
              flex: 1,
            }}>
              <div style={{
                display: 'grid',
                gap: '4px',
              }}>
                {localeOptions.map((option, index) => {
                  // Sprach-Gruppen-Header anzeigen (außer für die aktuelle Auswahl oben)
                  const prevOption = index > 0 ? localeOptions[index - 1] : null;
                  const showGroupHeader = !option.isSelected && (
                    !prevOption || prevOption.isSelected || prevOption.languageCode !== option.languageCode
                  );

                  return (
                    <div key={`${option.countryCode}-${option.languageCode}`}>
                      {showGroupHeader && (
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: 600,
                          color: 'var(--text-tertiary)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          padding: '12px 16px 4px',
                          marginTop: index > 0 ? '4px' : 0,
                        }}>
                          {option.language.nameNative}
                        </div>
                      )}
                  <button
                    onClick={() => handleSelect(option.countryCode, option.languageCode)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '14px 16px',
                      border: 'none',
                      borderRadius: 'var(--radius)',
                      background: option.isSelected
                        ? 'var(--primary-light)'
                        : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={(e) => {
                      if (!option.isSelected) {
                        e.currentTarget.style.background = 'var(--bg-secondary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!option.isSelected) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>
                      {option.country.flag}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: option.isSelected ? 600 : 500,
                        color: 'var(--text-primary)',
                        fontSize: '1rem',
                      }}>
                        {option.country.nameNative}
                        <span style={{
                          color: 'var(--text-tertiary)',
                          fontWeight: 400,
                          margin: '0 6px',
                        }}>·</span>
                        <span style={{
                          color: option.isSelected ? 'var(--text-primary)' : 'var(--text-secondary)',
                          fontWeight: option.isSelected ? 600 : 400,
                        }}>
                          {option.language.nameNative}
                        </span>
                      </div>
                    </div>
                    {option.isSelected && (
                      <Check size={20} style={{ color: 'var(--link)', flexShrink: 0 }} />
                    )}
                  </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Hard-Split: News-Language-Hint lebt in paperball.news. Hier
                braucht der LocaleSelector keinen Sonder-Hinweis — die UI-
                Sprache wirkt direkt auf alle Finance-Seiten. */}
          </div>
        </div>
      )}
    </>
  );
}
